import {
  Box,
  TextField,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  MenuItem,
  Chip,
  Checkbox,
} from '@mui/material';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';

import { useSetRecoilState } from 'recoil';
import {
  toastStateSelector,
  ToastSeverity,
  dialogState,
  usersAtom,
} from 'state';

import { GeneralIds, ModelTypes } from 'types/types';
import {
  getAddUserValidationSchema,
  initialAddUserValues,
} from 'forms/AddUser';
import { mutation } from 'operations/mutation';
import { useState } from 'react';
import { Role, SubProject, User } from 'api/models';
import { useParams } from 'react-router-dom';
import { query } from 'operations';
import ClearableSelect from 'components/ClearableSelect/ClearableSelect';
import useQuery from 'hooks/useQuery';

export function AddUserDialog() {
  const closeDialog = useSetRecoilState(dialogState);
  const setUserAtom = useSetRecoilState(usersAtom);
  const { t, i18n } = useTranslation();
  const setToast = useSetRecoilState(toastStateSelector);
  const { projectId } = useParams<GeneralIds>();

  const [roles, setRoles] = useState<Role[]>([]);

  const { data: subProjects = [] } = useQuery<SubProject[]>({
    type: ModelTypes.subProject,
    ids: {
      projectId,
    },
  });

  const defaultSubProjectId = subProjects[0]?.id;

  useQuery<Role[]>({
    type: ModelTypes.role,
    ids: { projectId, id: defaultSubProjectId },
    dependencies: [defaultSubProjectId],
    set: {
      data: setRoles,
    },
  });

  const formik = useFormik({
    initialValues: {
      ...initialAddUserValues,
      subProjectId: defaultSubProjectId,
    },
    enableReinitialize: true,
    validationSchema: getAddUserValidationSchema(t),
    onSubmit: async (values, { resetForm }) => {
      const { email, subProjectId, roles } = values;

      try {
        await mutation({
          type: ModelTypes.user,
          body: {
            id: '',
            userId: email,
            roles,
            email,
            subProjectId,
            preferredLanguage: i18n.language,
          },
          ids: {
            projectId,
          },
        });

        const users = await query<User[]>({
          type: ModelTypes.user,
          ids: {
            projectId,
          },
        });

        setUserAtom(users.data);

        resetForm();
        closeDialog({ isOpen: false });

        setToast({
          isOpen: true,
          message: t('general.success.save'),
          severity: ToastSeverity.success,
        });
      } catch (e: any) {
        const errorCode = e.response?.data?.error?.[0].code;
        setToast({
          isOpen: true,
          message: i18n.exists(`errors.${errorCode}`)
            ? t(`errors.${errorCode}`)
            : t('general.errors.save'),
          severity: ToastSeverity.error,
        });
      }
    },
  });

  return (
    <Box>
      <DialogTitle>{t('dialogs.addUser.title')}</DialogTitle>
      <DialogContent>
        <form>
          <TextField
            fullWidth
            margin="normal"
            label={t('general.email') + ' *'}
            type="e-mail"
            name="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
          />

          <ClearableSelect
            sx={{ mt: 2 }}
            fullWidth
            isClearable={true}
            onClear={() => {
              formik.setFieldValue('subProject', null);
              setRoles([]);
              formik.setFieldValue('roles', []);
            }}
            label={t('general.subProject') + ' *'}
            name="subProject"
            value={formik.values.subProjectId}
            onChange={async (e: React.ChangeEvent<{ value: string }>) => {
              formik.setFieldValue('subProjectId', e.target.value);

              const { data: roles } = await query<Role[]>({
                type: ModelTypes.role,
                ids: { id: e.target.value, projectId },
              });

              setRoles(roles);
            }}
            error={
              formik.touched.subProjectId && Boolean(formik.errors.subProjectId)
            }
            helperText={
              formik.touched.subProjectId && formik.errors.subProjectId
            }
            renderValue={subProjectId => {
              return (
                subProjects.find(subProject => subProject.id === subProjectId)
                  ?.name ?? ''
              );
            }}
          >
            {subProjects?.map(subProject => (
              <MenuItem key={subProject.id} value={subProject.id}>
                {subProject.name}
              </MenuItem>
            ))}
          </ClearableSelect>

          <ClearableSelect
            sx={{ mt: 2 }}
            fullWidth
            multiple={true}
            disabled={roles.length === 0}
            isClearable={true}
            onClear={() => {
              formik.setFieldValue('roles', []);
            }}
            label={t('general.permissions') + ' *'}
            name="roles"
            value={formik.values.roles}
            onChange={e => formik.setFieldValue('roles', e.target.value)}
            renderValue={() => {
              return formik.values.roles?.map(value => (
                <Chip
                  key={`add-user-dialog:${value.id}-value-chip`}
                  sx={{ mr: 1 }}
                  label={value.name}
                  color={'primary'}
                />
              ));
            }}
            error={formik.touched.roles && Boolean(formik.errors.roles)}
            helperText={Boolean(formik.touched.roles && formik.errors.roles)}
          >
            {roles?.map(role => (
              // @ts-ignore
              <MenuItem key={role.id} value={role}>
                <Checkbox checked={formik.values.roles.includes(role)} />
                {role.name}
              </MenuItem>
            ))}
          </ClearableSelect>
        </form>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => closeDialog({ isOpen: false })}
          color="secondary"
          variant="contained"
          sx={{ mb: 1 }}
        >
          {t('general.cancel')}
        </Button>
        <Button
          type="submit"
          color="primary"
          variant="contained"
          onClick={formik.submitForm}
          sx={{ mr: 2, mb: 1 }}
        >
          {t('general.create')}
        </Button>
      </DialogActions>
    </Box>
  );
}
